import React from "react";
import {
  Box,
  Tabs,
  Tab,
  IconButton,
  Typography,
  List,
  ListItem,
  CircularProgress,
  Tooltip,
  Theme,
} from "@mui/material";

import { Panel, InfoTooltip } from "../../components";
import ComponentLoader from "../../components/ComponentLoader/ComponentLoader";
import EmptyState from "../../components/EmptyState";
import {
  useAccountInsightsPanel,
  FormattedInsight,
} from "./AccountInsightsPanel.hook";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import {
  InfoOutline,
  PauseCircleOutline,
  Search,
  FileNew,
  Edit,
  CircleCheck,
  Block,
  AddToQueue,
  Airplay,
  CheckAll,
  CalendarCheck,
  ErrorOutline,
  CalendarEvent,
  CloseBig,
  Refresh02,
} from "@tiny/icons";

const statusIcons: Record<string, JSX.Element> = {
  CREATED: <FileNew color="primary" fontSize="inherit" />,
  UPDATED: <Edit color="primary" fontSize="inherit" />,
  DELETED: <CloseBig color="error" fontSize="inherit" />,
  ACTIVE: <CircleCheck color="success" fontSize="inherit" />,
  INACTIVE: <Block color="error" fontSize="inherit" />,
  QUEUED: <AddToQueue color="info" fontSize="inherit" />,
  LAUNCHED: <Airplay color="primary" fontSize="inherit" />,
  COMPLETED: <CheckAll color="success" fontSize="inherit" />,
  SCHEDULED: <CalendarCheck color="info" fontSize="inherit" />,
  FAILED: <ErrorOutline color="error" fontSize="inherit" />,
  PAUSED: <PauseCircleOutline color="warning" fontSize="inherit" />,
  RECURRING: <CalendarEvent color="secondary" fontSize="inherit" />,
  STOPPED: <Block color="error" fontSize="inherit" />,
};

const statusText: Record<string, string> = {
  CREATED: "created",
  UPDATED: "updated",
  DELETED: "deleted",
  ACTIVE: "activated",
  INACTIVE: "deactivated",
  QUEUED: "queued",
  LAUNCHED: "launched",
  COMPLETED: "completed",
  SCHEDULED: "scheduled",
  FAILED: "failed",
  PAUSED: "paused",
  RECURRING: "recurring",
  STOPPED: "stopped",
};

function AccountInsightsPanel(): JSX.Element {
  const {
    tabValue,
    handleTabChange,
    insights,
    isLoading,
    isFetching,
    hasMore,
    handleRefresh,
    handleInsightClick,
    loadMore,
    tabLabels,
  } = useAccountInsightsPanel();

  const lastInsightElementRef = useInfiniteScroll({
    isFetching,
    hasMore,
    loadMore,
  });

  const renderInsight = (
    insight: FormattedInsight,
    index: number
  ): JSX.Element => {
    const isLastElement = index === insights.length - 1;

    return (
      <ListItem
        key={`${insight.id}-${insight.eventTime}`}
        ref={isLastElement ? lastInsightElementRef : undefined}
        onClick={() =>
          insight?.status !== "DELETED"
            ? handleInsightClick(insight)
            : undefined
        }
        sx={{
          display: "flex",
          alignItems: "center",
          py: 2,
          cursor: "pointer",
          position: "relative",
          pl: 4,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        {/* Timeline Line */}
        <Box
          sx={{
            position: "absolute",
            left: 30,
            top: 0,
            bottom: 0,
            width: "1px",
            backgroundColor: "grey.300",
            zIndex: 0,
          }}
        />

        <Box
          sx={{
            zIndex: 2,
            backgroundColor: "grey.20",
            borderRadius: "50%",
            mr: 4,
            fontSize: "16px",
            p: 2,
            my: 0.5,
            display: "flex",
            alightItems: "center",
            justifyContent: "center",
          }}
        >
          {statusIcons[insight.status] || (
            <InfoOutline color="disabled" fontSize="inherit" />
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            flexWrap: "wrap",
            flexGrow: 1,
            gap: 1,
          }}
        >
          <Typography
            variant="label2R"
            color="text.secondary"
            sx={{ minWidth: "70px" }}
          >
            {insight.timestamp}
          </Typography>
          <Tooltip
            componentsProps={{
              popper: {
                sx: {
                  "& .MuiTooltip-tooltip": {
                    padding: 2,
                  },
                },
              },
            }}
            children={
              <Typography
                variant="caption"
                color="text.primary"
                sx={{
                  maxWidth: "200px",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  cursor: insight.name?.length > 30 ? "help" : "default",
                }}
              >
                {insight.name?.length > 30
                  ? `${insight.name.slice(0, 27)}...`
                  : insight.name}
              </Typography>
            }
            title={insight.name}
          ></Tooltip>
          <Typography variant="label2R" color="text.secondary">
            {statusText[insight?.status] || ""}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {insight.type}
          </Typography>
          {insight.status === "CREATED" && insight.creationSource != null && (
            <Typography variant="label2R" color="text.secondary">
              via {insight.creationSource}
            </Typography>
          )}
        </Box>
      </ListItem>
    );
  };

  const renderContent = () => {
    if (isLoading && insights.length === 0) {
      return <ComponentLoader height="300px" />;
    }

    if (!isLoading && insights.length === 0) {
      return (
        <Box
          sx={{
            height: "100%",
          }}
        >
          <EmptyState
            Icon={
              <Search
                sx={(theme: Theme) => ({
                  color: "grey.80",
                  width: theme.spacing(10),
                  height: theme.spacing(10),
                })}
              />
            }
            title="Account Insights"
            message="There are no recent Account Insights for the selected filter."
            height="100%"
          />
        </Box>
      );
    }

    return (
      <>
        <List disablePadding>
          {insights.map((insight, index) => renderInsight(insight, index))}
        </List>
        {isFetching && insights.length > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
      </>
    );
  };

  return (
    <Panel
      title="Account Insights"
      actions={
        <Box sx={{ display: "flex" }}>
          <IconButton
            aria-label="refresh"
            size="small"
            sx={{ mr: 1 }}
            onClick={handleRefresh}
            disabled={isFetching || isLoading}
          >
            <Refresh02 />
          </IconButton>
          <InfoTooltip
            message="Recent activities and changes across your account"
            variant="white"
            noInfoIcon
          >
            <IconButton sx={{ p: 0 }} size="small">
              <InfoOutline />
            </IconButton>
          </InfoTooltip>
        </Box>
      }
    >
      <Box sx={{ width: "100%", mt: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            aria-label="Filter insights by type"
            textColor="primary"
            indicatorColor="primary"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 5 }}
          >
            {tabLabels.map((label: string, index: number) => (
              <Tab
                key={label}
                sx={{ textTransform: "capitalize", minWidth: "auto" }}
                label={label.toLowerCase()}
                id={`insight-tab-${index}`}
                aria-controls={`insight-tabpanel-${index}`}
              />
            ))}
          </Tabs>
        </Box>

        <Box
          px={2}
          py={1}
          sx={{
            height: "400px",
            overflowY: "auto",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {renderContent()}
        </Box>
      </Box>
    </Panel>
  );
}

export default AccountInsightsPanel;
